import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PdfExportData, PdfTranslations, PdfChartImage } from '@/types/pdf/pdf.types';
import { formatNumber } from '@/utils/numberFormatter';
import { pdfChartRenderer } from './pdfChartRenderer';

// Color palette
const COLORS = {
  // Teal colors for carbon footprint
  tealLight: [240, 253, 250] as [number, number, number], // teal-50 background
  tealBorder: [153, 246, 228] as [number, number, number], // teal-200 border
  teal: [13, 148, 136] as [number, number, number], // teal-600 for titles
  tealDark: [19, 78, 74] as [number, number, number], // teal-800 for emphasis

  // Cyan colors for hydric footprint
  cyanLight: [236, 254, 255] as [number, number, number], // cyan-50 background
  cyanBorder: [165, 243, 252] as [number, number, number], // cyan-200 border
  cyan: [8, 145, 178] as [number, number, number], // cyan-600 for titles
  cyanDark: [21, 94, 117] as [number, number, number], // cyan-800 for emphasis

  // Additional colors
  accent: [245, 158, 11] as [number, number, number], // amber-500
  purple: [139, 92, 246] as [number, number, number], // violet-500
  dark: [31, 41, 55] as [number, number, number], // gray-800
  gray: [107, 114, 128] as [number, number, number], // gray-500
  light: [249, 250, 251] as [number, number, number], // gray-50
  white: [255, 255, 255] as [number, number, number],
  border: [229, 231, 235] as [number, number, number], // gray-200
};

/**
 * Load an image from URL and convert to base64, including dimensions
 */
async function loadImageAsBase64(url: string): Promise<{ data: string | null; format?: string; width?: number; height?: number; error?: string }> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    const contentType = response.headers.get('content-type') || blob.type;

    // Determine image format from MIME type
    let format = 'PNG'; // default
    if (contentType.includes('jpeg') || contentType.includes('jpg')) {
      format = 'JPEG';
    } else if (contentType.includes('png')) {
      format = 'PNG';
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;

        // Load image to get actual dimensions
        const img = new Image();
        img.onload = () => {
          resolve({
            data: base64String,
            format,
            width: img.width,
            height: img.height
          });
        };
        img.onerror = () => {
          resolve({
            data: base64String,
            format
          });
        };
        img.src = base64String;
      };
      reader.onerror = () => {
        resolve({
          data: null,
          error: 'Failed to convert image to base64'
        });
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading image:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error loading logo'
    };
  }
}


/**
 * Get activity type translation
 */
function getActivityTypeLabel(activityType: string, t: PdfTranslations): string {
  switch (activityType) {
    case 'extraction':
      return t.extractionActivity;
    case 'transformation':
      return t.transformationActivity;
    case 'extraction_and_transformation':
      return t.extractionAndTransformationActivity;
    default:
      return activityType;
  }
}

/**
 * Add page header with logo and company info
 */
function addHeader(
  doc: jsPDF,
  data: PdfExportData,
  t: PdfTranslations,
  logoBase64: string | null,
  logoFormat: string,
  logoWidth: number | undefined,
  logoHeight: number | undefined,
  pageWidth: number
): number {
  let yPos = 10;
  const margin = 12;

  // Logo - center aligned at the top with correct aspect ratio
  if (logoBase64 && logoWidth && logoHeight) {
    try {
      // Calculate logo dimensions maintaining aspect ratio
      const maxLogoHeight = 10; // Max height in mm
      const maxLogoWidth = 60; // Max width in mm
      const aspectRatio = logoWidth / logoHeight;

      let finalLogoHeight = maxLogoHeight;
      let finalLogoWidth = finalLogoHeight * aspectRatio;

      // If width exceeds max, scale down by width instead
      if (finalLogoWidth > maxLogoWidth) {
        finalLogoWidth = maxLogoWidth;
        finalLogoHeight = finalLogoWidth / aspectRatio;
      }

      const logoX = (pageWidth - finalLogoWidth) / 2; // Center the logo
      doc.addImage(logoBase64, logoFormat, logoX, yPos, finalLogoWidth, finalLogoHeight);
      yPos += finalLogoHeight + 2; // Space after logo
    } catch (e) {
      // If logo fails, continue without it
      console.error('Error adding logo:', e);
    }
  }

  // Title and company info - center aligned below logo
  const textX = pageWidth / 2; // Center align text

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text(t.title, textX, yPos + 4, { align: 'center' });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`${t.company}: ${data.companyName}`, textX, yPos + 9, { align: 'center' });
  doc.text(`${t.product}: ${data.productName} | ${t.year}: ${data.year}`, textX, yPos + 13, { align: 'center' });

  // Activity type info (use carbonData or hydricData)
  const activityType = data.carbonData?.activityType || data.hydricData?.activityType || '';
  if (activityType) {
    doc.text(`${t.activityType}: ${getActivityTypeLabel(activityType, t)}`, textX, yPos + 17, { align: 'center' });
  }

  // Horizontal line
  yPos = activityType ? yPos + 20 : yPos + 17;
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);

  return yPos + 5;
}

/**
 * Add a section title
 */
function addSectionTitle(
  doc: jsPDF,
  title: string,
  yPos: number,
  color: [number, number, number] = COLORS.teal
): number {
  const margin = 12;

  // Background bar
  doc.setFillColor(...color);
  doc.rect(margin, yPos, 3, 6, 'F');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text(title, margin + 6, yPos + 5);

  return yPos + 10;
}

/**
 * Add total footprint card
 */
function addTotalFootprintCard(
  doc: jsPDF,
  value: number,
  unit: string,
  label: string,
  yPos: number,
  pageWidth: number,
  color: [number, number, number] = COLORS.teal,
  lightColor: [number, number, number] = COLORS.tealLight,
  borderColor: [number, number, number] = COLORS.tealBorder
): number {
  const margin = 12;
  const cardWidth = pageWidth - (margin * 2);
  const cardHeight = 18;
  doc.setFillColor(...lightColor);
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, yPos, cardWidth, cardHeight, 2, 2, 'FD');

  // Label
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text(label, margin + 6, yPos + 7);

  // Value
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...color);
  doc.text(`${formatNumber(value)} ${unit}`, margin + 6, yPos + 15);

  return yPos + cardHeight + 5;
}

/**
 * Add a chart image to the PDF
 */
function addChartImage(
  doc: jsPDF,
  chartImage: PdfChartImage | null,
  yPos: number,
  pageWidth: number,
  maxWidth: number = 160
): number {
  if (!chartImage || !chartImage.dataUrl) {
    return yPos;
  }

  const margin = 12;
  const availableWidth = pageWidth - (margin * 2);

  // Calculate dimensions while maintaining aspect ratio
  const aspectRatio = chartImage.height / chartImage.width;
  let imgWidth = Math.min(maxWidth, availableWidth);
  let imgHeight = imgWidth * aspectRatio;

  // Center the image horizontally
  const xPos = margin + (availableWidth - imgWidth) / 2;

  try {
    // Charts are captured as JPEG; 'FAST' enables jsPDF's image compression.
    doc.addImage(chartImage.dataUrl, 'JPEG', xPos, yPos, imgWidth, imgHeight, undefined, 'FAST');
    return yPos + imgHeight + 4;
  } catch (error) {
    console.error('Error adding chart image to PDF:', error);
    return yPos;
  }
}

/**
 * Add phase breakdown table
 */
function addPhaseTable(
  doc: jsPDF,
  phases: Array<{ name: string; value: number; percentage: number }>,
  unit: string,
  t: PdfTranslations,
  yPos: number
): number {
  const margin = 12;

  if (phases.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(t.noData, margin, yPos + 5);
    return yPos + 10;
  }

  const tableData = phases.map((phase) => [
    phase.name,
    `${formatNumber(phase.value)} ${unit}`,
    `${formatNumber(phase.percentage, 1)}%`,
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [[t.tables.phase, t.tables.value, t.tables.percentage]],
    body: tableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: COLORS.teal,
      textColor: COLORS.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 2,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: COLORS.dark,
      cellPadding: 2,
    },
    alternateRowStyles: {
      fillColor: COLORS.light,
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 45, halign: 'right' },
      2: { cellWidth: 30, halign: 'right' },
    },
  });

  return (doc as any).lastAutoTable.finalY + 5;
}

/**
 * Add the data responsibility / liability declaration block.
 * Renders a light-gray boxed paragraph stating the company is responsible
 * for the data it submitted and that StonePRINT is not liable for inaccuracies.
 */
function addDataResponsibilityDeclaration(
  doc: jsPDF,
  t: PdfTranslations,
  yPos: number,
  pageWidth: number,
  pageHeight: number,
  data: PdfExportData,
  pageCount: { current: number; total: number }
): number {
  const margin = 12;
  const contentWidth = pageWidth - margin * 2;
  const padding = 4;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const title = t.dataResponsibilityTitle;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const bodyLines = doc.splitTextToSize(t.dataResponsibilityContent, contentWidth - padding * 2);

  // Estimate box height: title line + body lines + vertical padding.
  const lineHeight = 3.6;
  const boxHeight = padding * 2 + 5 + bodyLines.length * lineHeight;

  // Keep the whole block together on a page.
  yPos = checkPageBreak(doc, yPos + 6, boxHeight + 6, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);

  // Background box
  doc.setFillColor(...COLORS.light);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, yPos, contentWidth, boxHeight, 1.5, 1.5, 'FD');

  // Title
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text(title, margin + padding, yPos + padding + 3);

  // Body
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text(bodyLines, margin + padding, yPos + padding + 8);

  return yPos + boxHeight + 4;
}

/**
 * Add the per-leg transport breakdown table (quarry -> transformation).
 * Returns the new yPos; renders nothing and returns yPos unchanged when there
 * are no valid legs.
 */
function addTransportTable(
  doc: jsPDF,
  transports: Array<{ vehicleType: string; distance: number }>,
  t: PdfTranslations,
  yPos: number
): number {
  const margin = 12;
  const legs = (transports || []).filter((leg) => leg && leg.vehicleType && (Number(leg.distance) || 0) > 0);
  if (legs.length === 0) {
    return yPos;
  }

  const tableData = legs.map((leg, index) => [
    String(index + 1),
    leg.vehicleType,
    `${formatNumber(leg.distance, 0)} km`,
  ]);

  if (legs.length > 1) {
    const totalDistance = legs.reduce((sum, leg) => sum + (Number(leg.distance) || 0), 0);
    tableData.push(['', t.transportTotalDistance, `${formatNumber(totalDistance, 0)} km`]);
  }

  autoTable(doc, {
    startY: yPos,
    head: [['#', t.tables.vehicleType, t.tables.distance]],
    body: tableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: COLORS.accent,
      textColor: COLORS.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 2,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: COLORS.dark,
      cellPadding: 2,
    },
    alternateRowStyles: {
      fillColor: COLORS.light,
    },
    columnStyles: {
      0: { cellWidth: 12 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 40, halign: 'right' },
    },
  });

  return (doc as any).lastAutoTable.finalY + 5;
}

/**
 * Add material breakdown table
 */
function addMaterialTable(
  doc: jsPDF,
  materials: Array<{ name: string; quantity: number; unit: string; footprint: number }>,
  footprintUnit: string,
  t: PdfTranslations,
  yPos: number
): number {
  const margin = 12;

  if (materials.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(t.noData, margin, yPos + 5);
    return yPos + 10;
  }

  const tableData = materials.map((material) => [
    material.name,
    formatNumber(material.quantity),
    material.unit,
    `${formatNumber(material.footprint)} ${footprintUnit}`,
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [[t.tables.material, t.tables.quantity, t.tables.unit, t.tables.footprint]],
    body: tableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: COLORS.cyan,
      textColor: COLORS.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 2,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: COLORS.dark,
      cellPadding: 2,
    },
    alternateRowStyles: {
      fillColor: COLORS.light,
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 30, halign: 'right' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 45, halign: 'right' },
    },
  });

  return (doc as any).lastAutoTable.finalY + 5;
}

/**
 * Add allocation analysis section
 */
/**
 * Add allocation and efficiency section for carbon footprint
 */
function addCarbonAllocationSection(
  doc: jsPDF,
  allocation: { method: string; physicalRatio: number; economicRatio: number; decisionRatio: number },
  efficiency: { perUnit: number; perRevenue: number; unit: string },
  production: { quantity: number; unit: string; revenue: number },
  footprintUnit: string,
  t: PdfTranslations,
  yPos: number,
  pageWidth: number
): number {
  const margin = 12;
  const contentWidth = pageWidth - margin * 2;
  const revenuePerUnit = production.quantity > 0 ? production.revenue / production.quantity : 0;
  const methodLabel = allocation.method === 'physical' ? t.physicalMethod : t.economicMethod;

  doc.setFillColor(...COLORS.light);
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(margin, yPos, contentWidth, 70, 2, 2, 'FD');

  const col1X = margin + 5;
  const col2X = margin + contentWidth / 3;
  const col3X = margin + (contentWidth / 3) * 2;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);

  // Allocation info
  doc.text(`${t.allocationMethod}: ${methodLabel}`, col1X, yPos + 8);
  doc.text(`${t.decisionRatio}: ${formatNumber(allocation.decisionRatio, 2)}`, col2X, yPos + 8);

  // Ratios
  doc.text(`${t.physicalRatio}: ${formatNumber(allocation.physicalRatio * 100, 1)}%`, col1X, yPos + 18);
  doc.text(`${t.economicRatio}: ${formatNumber(allocation.economicRatio * 100, 1)}%`, col2X, yPos + 18);

  // Production metrics
  doc.text(`${t.production}: ${formatNumber(production.quantity)} ${production.unit}`, col1X, yPos + 32);
  doc.text(`${t.revenue}: €${formatNumber(production.revenue)}`, col2X, yPos + 32);
  doc.text(`${t.revenuePerUnit}: €${formatNumber(revenuePerUnit)}/${production.unit}`, col3X, yPos + 32);

  // Efficiency metrics title
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text(t.efficiencyMetrics, col1X, yPos + 46);

  // Footprint per unit
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`${t.footprintPerUnit}: ${formatNumber(efficiency.perUnit)} ${footprintUnit}/${efficiency.unit}`, col1X, yPos + 54);

  // Footprint per revenue
  doc.text(`${t.footprintPerRevenue}: ${formatNumber(efficiency.perRevenue)} ${footprintUnit}/k€`, col1X, yPos + 62);

  return yPos + 75;
}

/**
 * Add allocation and efficiency section for hydric footprint
 * Water always uses economic allocation - no decision ratio or physical ratio
 */
function addHydricAllocationSection(
  doc: jsPDF,
  allocation: { method: string; physicalRatio: number; economicRatio: number; decisionRatio: number },
  efficiency: { perUnit: number; perRevenue: number; unit: string },
  production: { quantity: number; unit: string; revenue: number },
  footprintUnit: string,
  t: PdfTranslations,
  yPos: number,
  pageWidth: number
): number {
  const margin = 12;
  const contentWidth = pageWidth - margin * 2;
  const revenuePerUnit = production.quantity > 0 ? production.revenue / production.quantity : 0;

  doc.setFillColor(...COLORS.light);
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(margin, yPos, contentWidth, 60, 2, 2, 'FD');

  const col1X = margin + 5;
  const col2X = margin + contentWidth / 3;
  const col3X = margin + (contentWidth / 3) * 2;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);

  // Allocation info
  doc.text(`${t.allocationMethod}: ${t.economicMethod}`, col1X, yPos + 8);
  doc.text(`${t.economicRatio}: ${formatNumber(allocation.economicRatio * 100, 1)}%`, col2X, yPos + 8);

  // Production metrics
  doc.text(`${t.production}: ${formatNumber(production.quantity)} ${production.unit}`, col1X, yPos + 20);
  doc.text(`${t.revenue}: €${formatNumber(production.revenue)}`, col2X, yPos + 20);
  doc.text(`${t.revenuePerUnit}: €${formatNumber(revenuePerUnit)}/${production.unit}`, col3X, yPos + 20);

  // Efficiency metrics title
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text(t.efficiencyMetrics, col1X, yPos + 36);

  // Footprint per unit
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`${t.footprintPerUnit}: ${formatNumber(efficiency.perUnit)} ${footprintUnit}/${efficiency.unit}`, col1X, yPos + 44);

  // Footprint per revenue
  doc.text(`${t.footprintPerRevenue}: ${formatNumber(efficiency.perRevenue)} ${footprintUnit}/k€`, col1X, yPos + 52);

  return yPos + 65;
}

/**
 * Add water-specific section (blue/gray water)
 */
function addWaterBreakdown(
  doc: jsPDF,
  blueWater: number | undefined,
  grayWater: number | undefined,
  t: PdfTranslations,
  yPos: number,
  pageWidth: number
): number {
  if (blueWater === undefined && grayWater === undefined) {
    return yPos;
  }

  const margin = 12;
  const colWidth = (pageWidth - margin * 2 - 8) / 2;

  // Blue water
  if (blueWater !== undefined && blueWater > 0) {
    doc.setFillColor(...COLORS.cyanLight);
    doc.setDrawColor(...COLORS.cyanBorder);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, yPos, colWidth, 16, 2, 2, 'FD');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    doc.text(t.blueWater, margin + 4, yPos + 6);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.cyan);
    doc.text(`${formatNumber(blueWater)} ${t.units.m3}`, margin + 4, yPos + 13);
  }

  // Gray water
  if (grayWater !== undefined && grayWater > 0) {
    const greyX = margin + colWidth + 8;
    doc.setFillColor(...COLORS.cyanLight);
    doc.setDrawColor(...COLORS.cyanBorder);
    doc.setLineWidth(0.3);
    doc.roundedRect(greyX, yPos, colWidth, 16, 2, 2, 'FD');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    doc.text(t.greyWater, greyX + 4, yPos + 6);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.cyan);
    doc.text(`${formatNumber(grayWater)} ${t.units.m3}`, greyX + 4, yPos + 13);
  }

  return yPos + 20;
}

/**
 * Add page footer
 */
function addFooter(
  doc: jsPDF,
  t: PdfTranslations,
  language: 'en' | 'pt',
  pageNum: number,
  totalPages: number,
  pageWidth: number,
  pageHeight: number,
  companyName: string
): void {
  const margin = 15;
  const footerY = pageHeight - 8; // Moved up to make room for logos section

  // Footer line
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

  // Footer text
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(150, 150, 150);

  // Left: footer message with company name
  const footerMessage = t.footer.replace('{companyName}', companyName);
  doc.text(footerMessage, margin, footerY);

  // Right: page number and date
  const dateStr = new Date().toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-GB');
  const pageText = `${t.page} ${pageNum} ${t.of} ${totalPages} | ${t.generatedOn} ${dateStr}`;
  const textWidth = doc.getTextWidth(pageText);
  doc.text(pageText, pageWidth - margin - textWidth, footerY);
}

/**
 * Add footer logos section to PDF - uses captured Footer component image
 * Positioned above the text footer on last page
 */
function addFooterLogos(
  doc: jsPDF,
  footerImage: PdfChartImage | null,
  pageWidth: number,
  pageHeight: number
): void {
  if (!footerImage || !footerImage.dataUrl) {
    console.warn('[PDF Export] Footer image not available');
    return;
  }

  const margin = 15;
  const availableWidth = pageWidth - (margin * 2);
  
  // Calculate dimensions while maintaining aspect ratio
  // Footer should be reasonably sized - max width of 180mm
  const maxWidth = 180;
  const aspectRatio = footerImage.height / footerImage.width;
  let imgWidth = Math.min(maxWidth, availableWidth);
  let imgHeight = imgWidth * aspectRatio;

  // Center the footer horizontally
  const xPos = margin + (availableWidth - imgWidth) / 2;
  
  // Position footer above the text footer area
  // Text footer is at pageHeight - 8, position logos well above that
  const yPos = pageHeight - imgHeight - 10;

  try {
    doc.addImage(footerImage.dataUrl, 'PNG', xPos, yPos, imgWidth, imgHeight);
  } catch (e) {
    console.error('Error adding footer image to PDF:', e);
  }
}

/**
 * Check if we need a new page
 */
function checkPageBreak(
  doc: jsPDF,
  currentY: number,
  neededSpace: number,
  pageHeight: number,
  t: PdfTranslations,
  language: 'en' | 'pt',
  pageWidth: number,
  pageCount: { current: number; total: number },
  companyName: string
): number {
  const footerSpace = 25; // Reserve space for footer

  if (currentY + neededSpace > pageHeight - footerSpace) {
    // Add footer to current page
    addFooter(doc, t, language, pageCount.current, pageCount.total, pageWidth, pageHeight, companyName);

    // Add new page
    doc.addPage();
    pageCount.current++;

    return 15; // Reset Y position for new page
  }

  return currentY;
}

/**
 * Build the complete PDF document with charts
 */
async function buildPdfDocument(data: PdfExportData, t: PdfTranslations): Promise<jsPDF> {
  console.log('[PDF Export] Starting PDF generation with charts...');

  // STEP 1: Capture all charts first
  let carbonPieChart: PdfChartImage | null = null;
  let carbonBarChart: PdfChartImage | null = null;
  let hydricPieChart: PdfChartImage | null = null;
  let hydricBarChart: PdfChartImage | null = null;

  try {
    // Capture carbon charts
    if (data.carbonData && data.carbonData.phases.length > 0) {
      console.log('[PDF Export] Capturing carbon pie chart...');
      const result = await pdfChartRenderer.capturePhasePieChart(
        data.carbonData.phases,
        'carbon-pie-chart'
      );
      if (result.success) {
        carbonPieChart = { dataUrl: result.dataUrl, width: result.width, height: result.height };
        console.log('[PDF Export] Carbon pie chart captured successfully');
      } else {
        console.warn('[PDF Export] Failed to capture carbon pie chart:', result.error);
      }
    }

    if (data.carbonData && data.carbonData.materials.length > 0) {
      console.log('[PDF Export] Capturing carbon bar chart...');
      const result = await pdfChartRenderer.captureMaterialBarChart(
        data.carbonData.materials,
        t.units.kgCO2e,
        'carbon-bar-chart',
        t.units.kgCO2e
      );
      if (result.success) {
        carbonBarChart = { dataUrl: result.dataUrl, width: result.width, height: result.height };
        console.log('[PDF Export] Carbon bar chart captured successfully');
      } else {
        console.warn('[PDF Export] Failed to capture carbon bar chart:', result.error);
      }
    }

    // Capture hydric charts
    if (data.hydricData && data.hydricData.phases.length > 0) {
      console.log('[PDF Export] Capturing hydric pie chart...');
      const result = await pdfChartRenderer.capturePhasePieChart(
        data.hydricData.phases,
        'hydric-pie-chart'
      );
      if (result.success) {
        hydricPieChart = { dataUrl: result.dataUrl, width: result.width, height: result.height };
        console.log('[PDF Export] Hydric pie chart captured successfully');
      } else {
        console.warn('[PDF Export] Failed to capture hydric pie chart:', result.error);
      }
    }

    if (data.hydricData && data.hydricData.materials.length > 0) {
      console.log('[PDF Export] Capturing hydric bar chart...');
      const result = await pdfChartRenderer.captureMaterialBarChart(
        data.hydricData.materials,
        t.units.m3,
        'hydric-bar-chart',
        t.units.m3
      );
      if (result.success) {
        hydricBarChart = { dataUrl: result.dataUrl, width: result.width, height: result.height };
        console.log('[PDF Export] Hydric bar chart captured successfully');
      } else {
        console.warn('[PDF Export] Failed to capture hydric bar chart:', result.error);
      }
    }
  } catch (error) {
    console.error('[PDF Export] Error capturing charts:', error);
    // Continue without charts if capture fails
  }

  // STEP 2: Build the PDF document
  console.log('[PDF Export] Building PDF document...');
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Load logo
  const logoResult = data.companyLogoUrl
    ? await loadImageAsBase64(data.companyLogoUrl)
    : { data: null, format: 'PNG' as const };

  const logoBase64 = logoResult.data;
  const logoFormat = logoResult.format || 'PNG';
  const logoWidth = logoResult.width;
  const logoHeight = logoResult.height;
  const logoError = logoResult.error;

  if (logoError) {
    console.warn('[PDF Export] Logo not loaded:', logoError);
  }

  // Capture footer component
  console.log('[PDF Export] Capturing footer...');
  let footerImage: PdfChartImage | null = null;
  try {
    const footerResult = await pdfChartRenderer.captureFooter();
    if (footerResult.success) {
      footerImage = { dataUrl: footerResult.dataUrl, width: footerResult.width, height: footerResult.height };
      console.log('[PDF Export] Footer captured successfully');
    } else {
      console.warn('[PDF Export] Failed to capture footer:', footerResult.error);
    }
  } catch (error) {
    console.error('[PDF Export] Error capturing footer:', error);
  }

  // Page counter (will be updated when we know total pages)
  const pageCount = { current: 1, total: 1 };

  // Start building document
  let yPos = addHeader(doc, data, t, logoBase64, logoFormat, logoWidth, logoHeight, pageWidth);

  // Transport legs (product-level; shown once regardless of footprint type)
  if (data.transports && data.transports.length > 0) {
    yPos = addSectionTitle(doc, t.transportSection, yPos, COLORS.accent);
    yPos = addTransportTable(doc, data.transports, t, yPos);
  }

  // Carbon Footprint Section
  if (data.carbonData && (data.footprintType === 'carbon' || data.footprintType === 'both')) {
    yPos = addSectionTitle(doc, t.carbonSection, yPos, COLORS.teal);

    yPos = addTotalFootprintCard(doc, data.carbonData.totalFootprint, t.units.kgCO2e, t.totalCarbonFootprint, yPos, pageWidth, COLORS.teal, COLORS.tealLight, COLORS.tealBorder);

    // Phase breakdown - Show chart AND table
    const estimatedTableHeight = data.carbonData.phases.length * 6 + 15; // Reduced table height estimate
    const totalNeededSpace = carbonPieChart ? 90 + estimatedTableHeight + 8 : estimatedTableHeight + 8;
    yPos = checkPageBreak(doc, yPos, totalNeededSpace, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.dark);
    doc.text(t.phaseCarbonBreakdown, 12, yPos);
    yPos += 4;

    // Add chart if available
    if (carbonPieChart) {
      yPos = addChartImage(doc, carbonPieChart, yPos, pageWidth, 130);
    }

    yPos = addPhaseTable(doc, data.carbonData.phases, t.units.kgCO2e, t, yPos);

    // Material breakdown - Show chart AND table
    if (data.carbonData.materials.length > 0) {
      yPos = checkPageBreak(doc, yPos, carbonBarChart ? 100 : 50, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.dark);
      doc.text(t.materialCarbonBreakdown, 12, yPos);
      yPos += 4;

      // Add chart if available
      if (carbonBarChart) {
        yPos = addChartImage(doc, carbonBarChart, yPos, pageWidth, 140);
        yPos = checkPageBreak(doc, yPos, 40, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
      }

      // Always add table for detailed data
      yPos = addMaterialTable(doc, data.carbonData.materials, t.units.kgCO2e, t, yPos);
    }

    // Carbon Allocation analysis
    yPos = checkPageBreak(doc, yPos, 80, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.dark);
    doc.text(t.allocationAnalysis, 12, yPos);
    yPos += 4;
    yPos = addCarbonAllocationSection(
      doc,
      data.carbonData.allocation,
      data.carbonData.efficiency,
      data.production,
      t.units.kgCO2e,
      t,
      yPos,
      pageWidth
    );
  }

  // Hydric Footprint Section
  if (data.hydricData && (data.footprintType === 'hydric' || data.footprintType === 'both')) {
    // Add some spacing between sections
    if (data.footprintType === 'both') {
      yPos += 6;
      yPos = checkPageBreak(doc, yPos, 80, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
    }

    yPos = addSectionTitle(doc, t.hydricSection, yPos, COLORS.cyan);

    yPos = addTotalFootprintCard(doc, data.hydricData.totalFootprint, t.units.m3, t.totalHydricFootprint, yPos, pageWidth, COLORS.cyan, COLORS.cyanLight, COLORS.cyanBorder);

    // Water breakdown (blue/grey)
    yPos = addWaterBreakdown(doc, data.hydricData.blueWater, data.hydricData.greyWater, t, yPos, pageWidth);

    // Phase breakdown - Show chart AND table
    yPos = checkPageBreak(doc, yPos, hydricPieChart ? 90 : 50, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.dark);
    doc.text(t.phaseHydricBreakdown, 12, yPos);
    yPos += 4;

    // Add chart if available
    if (hydricPieChart) {
      yPos = addChartImage(doc, hydricPieChart, yPos, pageWidth, 130);
      yPos = checkPageBreak(doc, yPos, 40, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
    }

    // Always add table for detailed data
    yPos = addPhaseTable(doc, data.hydricData.phases, t.units.m3, t, yPos);

    // Material breakdown - Show chart AND table
    if (data.hydricData.materials.length > 0) {
      yPos = checkPageBreak(doc, yPos, hydricBarChart ? 100 : 50, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.dark);
      doc.text(t.materialHydricBreakdown, 12, yPos);
      yPos += 4;

      // Add chart if available
      if (hydricBarChart) {
        yPos = addChartImage(doc, hydricBarChart, yPos, pageWidth, 140);
        yPos = checkPageBreak(doc, yPos, 40, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
      }

      // Always add table for detailed data
      yPos = addMaterialTable(doc, data.hydricData.materials, t.units.m3, t, yPos);
    }

    // Hydric Allocation analysis
    yPos = checkPageBreak(doc, yPos, 70, pageHeight, t, data.language, pageWidth, pageCount, data.companyName);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.dark);
    doc.text(t.allocationAnalysis, 12, yPos);
    yPos += 4;
    yPos = addHydricAllocationSection(
      doc,
      data.hydricData.allocation,
      data.hydricData.efficiency,
      data.production,
      t.units.m3,
      t,
      yPos,
      pageWidth
    );
  }

  // Data responsibility / liability declaration
  yPos = addDataResponsibilityDeclaration(doc, t, yPos, pageWidth, pageHeight, data, pageCount);

  // Update total pages and add footer to all pages
  pageCount.total = doc.getNumberOfPages();

  // Add footer logos on the last page only (positioned above footer)
  doc.setPage(pageCount.total);
  addFooterLogos(doc, footerImage, pageWidth, pageHeight);

  // Add footer to all pages
  for (let i = 1; i <= pageCount.total; i++) {
    doc.setPage(i);
    addFooter(doc, t, data.language, i, pageCount.total, pageWidth, pageHeight, data.companyName);
  }

  console.log('[PDF Export] PDF generation complete');
  return doc;
}

/**
 * Service for exporting shared results as PDF
 */
export const pdfExportService = {
  /**
   * Export footprint data to PDF
   * @param data - The PDF export data
   * @param translations - Translations for PDF content (from locale files)
   */
  exportToPdf: async (data: PdfExportData, translations: PdfTranslations): Promise<void> => {
    const doc = await buildPdfDocument(data, translations);
    
    // Generate filename
    const cleanCompanyName = data.companyName.replace(/[^a-zA-Z0-9]/g, '_');
    const cleanProductName = data.productName.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `${cleanCompanyName}_${cleanProductName}_${data.year}_footprint.pdf`;
    
    // Save the PDF
    doc.save(filename);
  },
  
  /**
   * Get PDF as blob (useful for previews or uploads)
   * @param data - The PDF export data
   * @param translations - Translations for PDF content (from locale files)
   */
  getPdfBlob: async (data: PdfExportData, translations: PdfTranslations): Promise<Blob> => {
    const doc = await buildPdfDocument(data, translations);
    return doc.output('blob');
  },
};
